/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { Platform, View } from "react-native";
import { Feather } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useColorScheme as useNwColorScheme } from "nativewind";

import { Button } from "~/ui/Button";

const colorSettings = ["light", "dark", "system"] as const;

type ColorSetting = (typeof colorSettings)[number];

const ColorSettingsContext = React.createContext<{
  colorSetting: ColorSetting;
  setColorSetting: (setting: ColorSetting) => void;
} | null>(null);

export function ColorSettingsProvider(props: { children: React.ReactNode }) {
  const [colorSetting, setColorSettingState] =
    React.useState<ColorSetting>("dark");

  const { setColorScheme: setNWColorScheme } = useNwColorScheme();

  const setColorSetting = React.useCallback(
    async (colorSetting: ColorSetting) => {
      setNWColorScheme(colorSetting);
      setColorSettingState(colorSetting);
      if (colorSetting === "system") {
        await AsyncStorage.removeItem("colorSetting");
      } else {
        await AsyncStorage.setItem("colorSetting", colorSetting);
      }
      if (Platform.OS === "web") {
        // @ts-expect-error TODO update global window types
        window.applyColorModeToDocument();
      }
    },
    [setNWColorScheme],
  );

  // React.useEffect(() => {
  //   (async () => {
  //     const storedSetting = await AsyncStorage.getItem("colorSetting");
  //     if (isColorSetting(storedSetting)) {
  //       setColorSetting(storedSetting);
  //     }
  //   })();
  // }, [setColorSetting]);

  return (
    <ColorSettingsContext.Provider
      value={{
        colorSetting: "dark",
        setColorSetting,
      }}
    >
      {props.children}
    </ColorSettingsContext.Provider>
  );
}

export function useColorSettings() {
  const context = React.useContext(ColorSettingsContext);
  if (!context) {
    throw new Error(
      "useColorSettings must be used within a ColorSettingsProvider",
    );
  }
  return context;
}

function isColorSetting(scheme?: string | null): scheme is ColorSetting {
  // @ts-expect-error duh it's a typeguard
  return typeof scheme === "string" && colorSettings.includes(scheme);
}

export function ColorSettingsSwitcher() {
  const { colorSetting, setColorSetting } = useColorSettings();

  return (
    <View className="border-input-border flex flex-row gap-px rounded-lg border p-px">
      <Button
        onPress={() => setColorSetting("system")}
        variant={colorSetting === "system" ? "primary" : "secondary"}
        size="small"
        iconBefore={<Feather name="smartphone" size={16} />}
        aria-label="System color mode"
        className="rounded-md border-transparent"
      />
      <Button
        onPress={() => setColorSetting("light")}
        variant={colorSetting === "light" ? "primary" : "secondary"}
        size="small"
        iconBefore={<Feather name="sun" size={16} />}
        aria-label="Light mode"
        className="rounded-md border-transparent"
      />
      <Button
        onPress={() => setColorSetting("dark")}
        variant={colorSetting === "dark" ? "primary" : "secondary"}
        size="small"
        iconBefore={<Feather name="moon" size={16} />}
        aria-label="Dark mode"
        className="rounded-md border-transparent"
      />
    </View>
  );
}
